import React, { useEffect, useRef, useState } from 'react';
import * as http from '../../../../utils/http';
import * as translator from '../../../../utils/translator';
import './MyAccountPendingActions.css';
import './UkVerificationTasks.css';
import PhotoInstructionsModal from './PhotoInstructionsModal';
import PhotoUploadModal from './PhotoUploadModal';
import DoseProofModal from './DoseProofModal';
import { MainTracker } from '../../../../PixelTrackers/MainTracker';
import { trackOncePerSession } from '../../../../PixelTrackers/trackingUtils';
import { PendingActionsData, VerificationTaskType } from './types';
import infoIcon from './icons/task-info.svg';
import chevronIcon from './icons/task-chevron.svg';
import editIcon from './icons/task-edit.svg';

const FLOW_VERSION = 'hl12997';

// to do / active = open (the first open task is the active one), sent = submitted and being checked, done = verified.
type TaskState = 'locked' | 'todo' | 'active' | 'sent' | 'done';

interface Task {
  type: VerificationTaskType;
  state: TaskState;
  title: string;
  description: string;
  onOpen?: () => void;
  onEdit?: () => void;
}

interface Props {
  hashID: string;
  countryID: string;
  language: string;
  initialData: PendingActionsData;
  onOrderChanged?: () => void;
}

/**
 * HL-12997 Part B: UK WL post-purchase verification as task cards — full body photos (first), ID verification
 * (locked until the photos are done) and proof of previous use (only when the ordered dose needs it, never locked).
 */
const UkVerificationTasks: React.FC<Props> = ({ hashID, countryID, language, initialData, onOrderChanged }) => {
  const t = (key: string) => translator.translate(language, 'my_account', `VerificationTasks/${key}`);
  const [data, setData] = useState<PendingActionsData>(initialData);
  const [refreshing, setRefreshing] = useState(false);
  const [photoScreen, setPhotoScreen] = useState<'guide' | 'upload' | null>(null);
  const [showProof, setShowProof] = useState(false);
  const [proofSwitched, setProofSwitched] = useState(false);
  const [jumioSrc, setJumioSrc] = useState<string | null>(null);
  const [jumioLoading, setJumioLoading] = useState(false);
  const [jumioSubmitted, setJumioSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const completedRef = useRef(new Set<VerificationTaskType>());
  const jumioRef = useRef<HTMLDivElement>(null);

  const trackingProps = (taskType: VerificationTaskType) => ({
    order_hash_id: hashID,
    task_type: taskType,
    customer_type: data.trackingContext?.customer_type,
    prior_use_current: data.trackingContext?.prior_use_current,
    prior_use_ever: data.trackingContext?.prior_use_ever,
    flow_version: FLOW_VERSION,
  });

  const trackTaskViewed = (taskType: VerificationTaskType) => {
    trackOncePerSession('custom', 'Verification task viewed', trackingProps(taskType), 3, `Verification task viewed:${hashID}:${taskType}`);
  };

  const trackTaskCompleted = (taskType: VerificationTaskType, fileType?: string) => {
    if (completedRef.current.has(taskType)) return;
    completedRef.current.add(taskType);
    MainTracker.track('custom', 'Verification task completed', {
      ...trackingProps(taskType),
      ...(fileType ? { file_type: fileType } : {}),
    });
  };

  const refresh = (withSkeleton = false) => {
    if (withSkeleton) setRefreshing(true);
    return http
      .getFromServer(`${countryID}/account/orders/${hashID}/pending-actions`, [http.MIDDLEWARES.CUSTOMER_ID])
      .then((res: any) => {
        const next: PendingActionsData = res.data;
        setData((prev) => next.isWaitingActions ? next : {
          // The order left verification (every task done): keep the task list and show it as done.
          ...prev,
          needsPhotoUpload: false,
          needsIdVerification: false,
          idVerificationInProgress: false,
          needsDoseProof: false,
          isWaitingActions: false,
          frontPhotoDataUrl: next.frontPhotoDataUrl ?? prev.frontPhotoDataUrl,
          sidePhotoDataUrl: next.sidePhotoDataUrl ?? prev.sidePhotoDataUrl,
        });
      })
      .catch(() => {})
      .finally(() => setRefreshing(false));
  };

  useEffect(() => {
    if (!jumioSrc) return;
    jumioRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const handleMessage = (event: MessageEvent) => {
      try {
        const message = event.data && typeof event.data === 'string' ? JSON.parse(event.data) : null;
        if (message?.payload?.value === 'success') {
          setJumioSubmitted(true);
          setJumioSrc(null);
          trackTaskCompleted('id');
          refresh(true);
        }
      } catch {}
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [jumioSrc]);

  const handleStartJumio = async () => {
    trackTaskViewed('id');
    setJumioLoading(true);
    setErrorMessage(null);
    try {
      const res: any = await http.getFromServer(
        `${countryID}/account/orders/${hashID}/jumio-verification`,
        [http.MIDDLEWARES.CUSTOMER_ID]
      );
      if (res.data.status === 'already_verified') {
        setJumioSubmitted(true);
        trackTaskCompleted('id');
        refresh(true);
      } else {
        setJumioSrc(res.data.jumioWorkflowSrc);
      }
    } catch {
      setErrorMessage(t('id_start_failed'));
    } finally {
      setJumioLoading(false);
    }
  };

  const openPhotoGuide = () => {
    trackTaskViewed('photos');
    MainTracker.track('custom', 'photo guide viewed', { customer_type: data.trackingContext?.customer_type });
    setPhotoScreen('guide');
  };

  const handlePhotosUploaded = (frontUrl: string, sideUrl: string, fileType?: string) => {
    setData((prev) => ({ ...prev, needsPhotoUpload: false, frontPhotoDataUrl: frontUrl, sidePhotoDataUrl: sideUrl }));
    trackTaskCompleted('photos', fileType);
    refresh();
  };

  const openProof = () => {
    trackTaskViewed('proof_of_use');
    setShowProof(true);
  };

  const handleProofSubmitted = (fileType?: string) => {
    setData((prev) => ({ ...prev, needsDoseProof: false }));
    trackTaskCompleted('proof_of_use', fileType);
    refresh();
  };

  const handleProofSwitched = () => {
    const switchData = data.doseProofSwitch;
    MainTracker.track('custom', 'wl_dose_proof_skipped_switch', {
      orderID: hashID,
      original_product: switchData?.originalProductTitle,
      new_product: switchData?.allowedProductTitle,
      customer_type: data.trackingContext?.customer_type,
      prior_use_current: data.trackingContext?.prior_use_current,
      prior_use_ever: data.trackingContext?.prior_use_ever,
    });
    // A-5: the proof task is removed; the order now holds the allowed dose at its price.
    setProofSwitched(true);
    setData((prev) => ({ ...prev, needsDoseProof: false, doseProofFileName: null, doseProofSwitch: null }));
    refresh(true);
    onOrderChanged?.();
  };

  /* ── Tasks in the design's order: photos, ID (locked until the photos are done), proof (never locked) ── */
  const hasPhotos = !!(data.frontPhotoDataUrl || data.sidePhotoDataUrl);
  const photosDone = !data.needsPhotoUpload && hasPhotos;
  const hasPhotoTask = data.needsPhotoUpload || hasPhotos;
  const hasProofTask = !proofSwitched && (!!data.needsDoseProof || !!data.doseProofFileName);
  const proofDone = !data.needsDoseProof && !!data.doseProofFileName;
  const hasIdTask = data.requiresIdVerification || data.needsIdVerification;
  const idSent = jumioSubmitted || !data.needsIdVerification;
  const idRetryable = data.idVerificationInProgress && !jumioSubmitted;
  const idVerified = idSent && !jumioSubmitted && !data.idVerificationInProgress;

  const tasks: Task[] = [];
  if (hasPhotoTask) {
    tasks.push(photosDone
      ? { type: 'photos', state: 'sent', title: t('photo_sent_title'), description: t('photo_sent_desc'), onEdit: () => setPhotoScreen('upload') }
      : { type: 'photos', state: 'active', title: t('photo_title'), description: t('photo_desc'), onOpen: openPhotoGuide });
  }
  if (hasIdTask) {
    if (idSent) {
      tasks.push({
        type: 'id',
        state: idVerified ? 'done' : 'sent',
        title: t('id_title'),
        description: idRetryable ? t('id_pending_desc') : t('id_desc'),
        onEdit: idRetryable && !jumioSrc && !jumioLoading ? handleStartJumio : undefined,
      });
    } else if (hasPhotoTask && !photosDone) {
      tasks.push({ type: 'id', state: 'locked', title: t('id_title'), description: t('id_locked_desc') });
    } else {
      tasks.push({
        type: 'id',
        state: 'active',
        title: t('id_title'),
        description: jumioLoading ? t('id_btn_loading') : t('id_desc'),
        onOpen: !jumioSrc && !jumioLoading ? handleStartJumio : undefined,
      });
    }
  }
  if (hasProofTask) {
    tasks.push(proofDone
      ? { type: 'proof_of_use', state: 'sent', title: t('proof_title'), description: data.doseProofFileName! }
      : { type: 'proof_of_use', state: 'active', title: t('proof_title'), description: t('proof_desc'), onOpen: openProof });
  }

  if (!tasks.length) return null;

  const firstOpen = tasks.find((task) => task.state === 'active');
  tasks.forEach((task) => {
    if (task.state === 'active' && task !== firstOpen) task.state = 'todo';
  });

  const total = tasks.length;
  const doneCount = tasks.filter((task) => task.state === 'sent' || task.state === 'done').length;
  const remaining = total - doneCount;
  const allDone = remaining === 0;
  const stepLabel = (type: VerificationTaskType) =>
    t('step_label')
      .replace('{{step}}', String(tasks.findIndex((task) => task.type === type) + 1))
      .replace('{{total}}', String(total));
  const deadlineDate = data.autoCancelAt
    ? new Date(data.autoCancelAt).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Europe/London' })
    : null;

  if (refreshing) {
    return (
      <div className="pending-actions-card vt-skeleton" aria-busy="true">
        <div className="vt-skeleton-line vt-skeleton-title" />
        <div className="vt-skeleton-line vt-skeleton-subtitle" />
        {tasks.map((task) => <div key={task.type} className="vt-skeleton-task" />)}
      </div>
    );
  }

  return (
    <>
      <div className="pending-actions-card vt-card">
        <div className="pending-actions-header">
          <p className="pending-actions-title vt-title">
            {allDone ? t('title_all_done') : t(`title_remaining_${Math.min(remaining, 3)}`)}
          </p>
          <p className="pending-actions-subtitle vt-subtitle">{allDone ? t('subtitle_all_done') : t('subtitle')}</p>
        </div>

        <div className="vt-progress">
          <div className="vt-progress-track">
            <div className="vt-progress-fill" style={{ width: `${(doneCount / total) * 100}%` }} />
          </div>
          <span className="vt-progress-label">
            {t('progress').replace('{{done}}', String(doneCount)).replace('{{total}}', String(total))}
          </span>
        </div>

        {!allDone && deadlineDate && (
          <div className="vt-deadline">
            <img src={infoIcon} alt="" className="vt-deadline-icon" />
            <p className="vt-deadline-text">{t('deadline').replace('{{date}}', deadlineDate)}</p>
          </div>
        )}

        <div className="vt-tasks">
          {tasks.map((task, index) => <TaskCard key={task.type} task={task} step={index + 1} language={language} />)}
        </div>

        {jumioSrc && (
          <div className="pending-actions-jumio-wrapper" ref={jumioRef}>
            <iframe src={jumioSrc} allow="camera; microphone" title="ID Verification" />
          </div>
        )}

        {errorMessage && <p className="pending-actions-error">{errorMessage}</p>}

        <div className="pending-actions-divider" />
        <p className="pending-actions-footer">{t('footer')}</p>
      </div>

      {photoScreen === 'guide' && (
        <PhotoInstructionsModal
          language={language}
          stepLabel={stepLabel('photos')}
          onClose={() => setPhotoScreen(null)}
          onContinue={() => setPhotoScreen('upload')}
        />
      )}

      {photoScreen === 'upload' && (
        <PhotoUploadModal
          hashID={hashID}
          countryID={countryID}
          language={language}
          isWaitingActions={data.isWaitingActions}
          initialFrontUrl={data.frontPhotoDataUrl}
          initialSideUrl={data.sidePhotoDataUrl}
          onClose={() => setPhotoScreen(null)}
          onBothUploaded={handlePhotosUploaded}
          stepLabel={stepLabel('photos')}
          uploadOnly
        />
      )}

      {showProof && (
        <DoseProofModal
          hashID={hashID}
          countryID={countryID}
          language={language}
          stepLabel={stepLabel('proof_of_use')}
          initialFileName={data.doseProofFileName ?? null}
          switchData={data.doseProofSwitch ?? null}
          onClose={() => setShowProof(false)}
          onUploaded={(fileName) => setData((prev) => ({ ...prev, doseProofFileName: fileName }))}
          onSubmitted={handleProofSubmitted}
          onSwitched={handleProofSwitched}
        />
      )}
    </>
  );
};

interface TaskCardProps {
  task: Task;
  step: number;
  language: string;
}

const TaskCard: React.FC<TaskCardProps> = ({ task, step, language }) => {
  const t = (key: string) => translator.translate(language, 'my_account', `VerificationTasks/${key}`);
  const content = (
    <>
      <span className={`vt-step vt-step-${task.state}`}>{step}</span>
      <span className="vt-task-text">
        <span className="vt-task-title">{task.title}</span>
        <span className="vt-task-desc">{task.description}</span>
      </span>
      <span className={`vt-badge vt-badge-${task.state}`}>{t(`badge_${task.state}`)}</span>
    </>
  );

  if (task.onOpen) {
    return (
      <button type="button" className={`vt-task vt-task-${task.state}`} onClick={task.onOpen}>
        {content}
        <img src={chevronIcon} alt="" className="vt-task-chevron" />
      </button>
    );
  }

  return (
    <div className={`vt-task vt-task-${task.state}`}>
      {content}
      {task.onEdit && (
        <button type="button" className="vt-task-edit" onClick={task.onEdit} aria-label={t('edit_aria')}>
          <img src={editIcon} alt="" />
        </button>
      )}
    </div>
  );
};

export default UkVerificationTasks;
